import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

export type PickerItem = {
  id: string;
  /** Główna etykieta wiersza. */
  primary: string;
  /** Drobny prefiks monospace — zwykle klucz naturalny SPS. */
  key?: string | number;
  /** Dodatek po prawej (np. plakietka „pobiera na 2 automatach"). */
  badge?: ReactNode;
};

/**
 * Dwukolumnowy przenośnik „przypisane ↔ dostępne".
 *
 * JEDNA implementacja używana przez wszystkie moduły relacyjne — celowo, bo pierwsza
 * wersja (skopiowana) miała błąd stale closure: `toggle` czytał `Set` przechwycony przy
 * renderze, więc dwa kliknięcia przed przerysowaniem gubiły jedno. Stan trzyma rodzic
 * i MUSI aktualizować go funkcyjnie (`setX(prev => …)`) — patrz `toggleIn` niżej.
 */
export function TransferPicker({
  assignedTitle,
  availableTitle,
  assigned,
  candidates,
  toAdd,
  toRemove,
  onToggleAdd,
  onToggleRemove,
  search,
  onSearchChange,
  capNote,
  loadingAssigned,
  loadingCandidates,
  emptyAssigned,
  emptyCandidates,
  candidatesTotal,
  hasMore,
  loadingMore,
  onLoadMore,
}: {
  assignedTitle: string;
  availableTitle: string;
  assigned: PickerItem[];
  candidates: PickerItem[];
  toAdd: Set<string>;
  toRemove: Set<string>;
  onToggleAdd: (id: string) => void;
  onToggleRemove: (id: string) => void;
  search: string;
  onSearchChange: (v: string) => void;
  capNote?: string;
  loadingAssigned?: boolean;
  loadingCandidates?: boolean;
  emptyAssigned: string;
  emptyCandidates: string;
  /**
   * Ilu jest WSZYSTKICH pasujących kandydatów. Podany — nagłówek pokazuje „widać N z M”,
   * bo lista ucięta bez tej liczby wygląda na kompletną.
   */
  candidatesTotal?: number;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}) {
  const { t } = useI18n();
  const wartownik = useRef<HTMLDivElement | null>(null);

  /**
   * DOCZYTYWANIE PRZY PRZEWIJANIU — obserwator na wartowniku pod ostatnim wierszem.
   *
   * ⚠️ `onLoadMore` NIE jest w zależnościach, tylko w referencji. Rodzic przekazuje tu zwykle
   * świeżą funkcję przy każdym renderze, więc lista zależności z `onLoadMore` odpinałaby
   * i podpinała obserwator po każdym doczytaniu — a przy ponownym podpięciu wartownik nadal
   * jest widoczny, więc obserwator odpala od razu i pobiera kolejną porcję. Efektem jest pętla,
   * która wciąga całą tabelę zamiast jednej porcji.
   */
  const doczytaj = useRef(onLoadMore);
  doczytaj.current = onLoadMore;

  const mozeWiecej = Boolean(hasMore) && !loadingMore && !loadingCandidates;
  useEffect(() => {
    const el = wartownik.current;
    if (!el || !mozeWiecej) return;
    const obs = new IntersectionObserver((wpisy) => {
      if (wpisy.some((w) => w.isIntersecting)) doczytaj.current?.();
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [mozeWiecej]);

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="flex flex-col rounded-lg border border-border">
        <div className="border-b border-border px-3 py-2 text-xs font-medium">
          {assignedTitle} ({assigned.length})
          {toRemove.size > 0 && <span className="ml-1 text-destructive">−{toRemove.size}</span>}
        </div>
        <div className="max-h-72 overflow-y-auto p-1">
          {loadingAssigned && <p className="p-2 text-xs text-muted-foreground">{t('common.loading')}</p>}
          {!loadingAssigned && assigned.length === 0 && <p className="p-2 text-xs text-muted-foreground">{emptyAssigned}</p>}
          {assigned.map((it) => {
            const marked = toRemove.has(it.id);
            return (
              <button
                key={it.id}
                type="button"
                aria-pressed={marked}
                onClick={() => onToggleRemove(it.id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-accent',
                  marked && 'bg-destructive/10 line-through',
                )}
              >
                <ArrowRight className="size-3 shrink-0 text-muted-foreground" />
                {it.key !== undefined && <span className="font-mono text-[10px] text-muted-foreground">{it.key}</span>}
                <span className="min-w-0 flex-1 truncate">{it.primary}</span>
                {it.badge}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col rounded-lg border border-border">
        <div className="border-b border-border px-3 py-2 text-xs font-medium">
          {/* ⚠️ „N z M", gdy znamy komplet. Sama liczba widocznych wierszy na uciętej liście
              wygląda jak komplet — a wtedy „nie mogę go znaleźć" jest nieodróżnialne
              od „nie ma go w systemie". */}
          {availableTitle} ({candidatesTotal !== undefined && candidatesTotal > candidates.length
            ? `${candidates.length} / ${candidatesTotal}`
            : candidates.length})
          {toAdd.size > 0 && <span className="ml-1 text-[var(--success)]">+{toAdd.size}</span>}
        </div>
        <div className="border-b border-border p-2">
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('table.search')}
            className="h-7 text-xs"
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {loadingCandidates && <p className="p-2 text-xs text-muted-foreground">{t('common.loading')}</p>}
          {!loadingCandidates && candidates.length === 0 && (
            <p className="p-2 text-xs text-muted-foreground">{emptyCandidates}</p>
          )}
          {candidates.map((it) => {
            const marked = toAdd.has(it.id);
            return (
              <button
                key={it.id}
                type="button"
                aria-pressed={marked}
                onClick={() => onToggleAdd(it.id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-accent',
                  marked && 'bg-[var(--success)]/10 font-medium',
                )}
              >
                <ArrowLeft className="size-3 shrink-0 text-muted-foreground" />
                {it.key !== undefined && <span className="font-mono text-[10px] text-muted-foreground">{it.key}</span>}
                <span className="min-w-0 flex-1 truncate">{it.primary}</span>
                {it.badge}
              </button>
            );
          })}

          {/* Wartownik doczytywania. Przycisk zostaje obok obserwatora, bo obserwator nie
              zadziała bez myszy/kółka — a listę trzeba dać się przejrzeć też z klawiatury. */}
          {hasMore && (
            <div ref={wartownik} className="p-1">
              <button
                type="button"
                onClick={() => onLoadMore?.()}
                disabled={loadingMore}
                className="flex w-full items-center justify-center gap-1.5 rounded px-2 py-1.5 text-[11px] text-muted-foreground transition-colors hover:bg-accent disabled:opacity-60"
              >
                {loadingMore && <Loader2 className="size-3 animate-spin" />}
                {loadingMore ? t('common.loading') : t('picker.more')}
              </button>
            </div>
          )}
        </div>
        {capNote && <p className="border-t border-border px-2 py-1.5 text-[10px] text-muted-foreground">{capNote}</p>}
      </div>
    </div>
  );
}

/**
 * Kanoniczny przełącznik zaznaczenia — **zawsze** aktualizacja funkcyjna.
 * Wersja czytająca `Set` z domknięcia gubiła kliknięcia (patrz komentarz wyżej).
 */
export function toggleIn(setter: React.Dispatch<React.SetStateAction<Set<string>>>, id: string): void {
  setter((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
}
